import type {AdsListPresentation} from '@frontend/modules/public/ads/AdsModuleTypes'
import {getFailSafeLocalStorage} from 'swayok-react-mdb-ui-kit/helpers/local_storage/getFailSafeLocalStorage'

// Доступ и управление данными в локальном хранилище.
export class LocalStorage {

    // Получить локацию, по которой в последний раз была фильтрация.
    // Это достоверный источник актуального ID локации, который можно использовать для
    // генерации URL к спискам объявлений и услуг.
    static getFilterLocation(): null | string {
        const location: string | null = getFailSafeLocalStorage().getItem('filter_location')
        if (!location) {
            return null
        }
        // Проверка, что это число или {slug}-{id}.
        if (location.match('^(\d+|.*?-\d+)$')) {
            return location
        }
        this.setFilterLocation(null)
        return null
    }

    // Сохранить/удалить локацию, по которой в последний раз была фильтрация.
    static setFilterLocation(locationIdOrSlugWithId: string | null): void {
        if (
            locationIdOrSlugWithId !== null
            && locationIdOrSlugWithId.trim() !== ''
        ) {
            getFailSafeLocalStorage().setItem(
                'filter_location',
                locationIdOrSlugWithId.trim()
            )
        } else {
            getFailSafeLocalStorage().removeItem('filter_location')
        }
    }

    // Получить вариант отображения списка объявлений.
    static getAdsListPresentation(fallback: AdsListPresentation): AdsListPresentation {
        return getFailSafeLocalStorage().getItem('ads_list_presentation') as AdsListPresentation
            ?? fallback
    }

    // Сохранить/удалить вариант отображения списка объявлений.
    static setAdsListPresentation(presentation: AdsListPresentation | null): void {
        if (presentation) {
            getFailSafeLocalStorage().setItem('ads_list_presentation', presentation)
        } else {
            getFailSafeLocalStorage().removeItem('ads_list_presentation')
        }
    }

    // Скрыть уведомление.
    static dismissNotification(name: string): void {
        this.setPermanentFlag('dismissed-' + name)
    }

    // Было ли уведомление закрыто?
    static isNotificationDismissed(name: string): boolean {
        return this.hasFlag('dismissed-' + name)
    }

    // Установить флаг без срока истечения.
    static setPermanentFlag(name: string): void {
        getFailSafeLocalStorage().setItem(this.getFlagName(name), '1')
    }

    // Установить флаг со сроком истечения.
    static setTempFlag(name: string, lifetimeSeconds: number): void {
        getFailSafeLocalStorage().setItem(
            this.getFlagName(name),
            String(Date.now() + (lifetimeSeconds * 1000))
        )
    }

    // Проверить есть ли активный флаг.
    static hasFlag(name: string): boolean {
        return this.getFlagRemainingLifetime(name) > 0
    }

    // Получить количество секунд до истечения срока жизни флага.
    static getFlagRemainingLifetime(name: string): number {
        const value: string | null = getFailSafeLocalStorage().getItem(this.getFlagName(name))
        if (value === null) {
            return 0
        }
        if (value === '1') {
            // Постоянный флаг: год.
            return 31536000
        }
        // Временный флаг.
        return Math.floor(Math.max(0, parseInt(value) - Date.now()) / 1000)
    }

    // Удалить флаг.
    static removeFlag(name: string): void {
        getFailSafeLocalStorage().removeItem(this.getFlagName(name))
    }

    // Проверить есть ли активный флаг и удалить его.
    static pullFlag(name: string): boolean {
        const exists: boolean = this.hasFlag(name)
        this.removeFlag(name)
        return exists
    }

    // Получить безопасное имя флага для сохранения в window.localStorage.
    private static getFlagName(name: string): string {
        return '_flag-' + name
    }
}

